import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, homeFor } from "@/context/AuthContext";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { CategoryPicker } from "@/components/CategoryPicker";
import { Btn, Card, Field, Input, Select, Textarea } from "@/components/Ui";
import { CountrySelect } from "@/components/CountrySelect";
import { EMIRATES, SUPPLIER_TYPES, UAE, errMsg, licenceLabel } from "@/lib/api";

const listify = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function Register() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { register } = useAuth();
  const schema = useSpecSchema();
  const [role, setRole] = useState(params.get("role") === "supplier" ? "supplier" : "buyer");
  const [f, setF] = useState({
    name: "", email: "", password: "", company_name: "", country: "United Arab Emirates",
    emirate: "", state: "", city: "", trade_licence: "", industry: "", website: "", contact_person: "", phone: "",
    supplier_type: "manufacturer", categories: [], subcategories: "", brands: "",
    markets_served: "", certifications: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (role === "supplier" && f.categories.length === 0) {
      setError("Select at least one product category — RFQ matching depends on it");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const user = await register({
        email: f.email,
        password: f.password,
        name: f.name,
        role,
        company: {
          name: f.company_name,
          country: f.country,
          emirate: f.emirate || null,
          state: f.state || null,
          city: f.city || null,
          trade_licence: f.trade_licence || null,
          industry: f.industry || null,
          website: f.website || null,
          contact_person: f.contact_person || f.name,
          phone: f.phone || null,
          supplier_type: role === "supplier" ? f.supplier_type : null,
          categories: f.categories,
          subcategories: listify(f.subcategories),
          brands: listify(f.brands),
          markets_served: listify(f.markets_served),
          certifications: listify(f.certifications),
        },
      });
      toast.success("Account created — your company is pending verification");
      navigate(homeFor(user.role), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-12" data-testid="register-page">
      <h1 className="font-display text-3xl font-black tracking-tight text-[#102A43] sm:text-4xl">
        Create your account
      </h1>
      <p className="mt-3 max-w-2xl text-sm text-[#667788] md:text-base">
        Register as a buyer to raise RFQs and compare offers, or as a manufacturer / supplier to
        receive matched enquiries. All company profiles are verified by the ADIT team before
        verified status is granted.
      </p>

      <div className="mt-7 flex gap-2">
        {[["buyer", "I am a buyer"], ["supplier", "I am a supplier / manufacturer"]].map(([v, l]) => (
          <button
            key={v}
            type="button"
            data-testid={`role-tab-${v}`}
            onClick={() => setRole(v)}
            className={`rounded-[9px] px-4 py-2.5 text-sm font-bold transition-colors ${
              role === v ? "bg-[#102A43] text-white" : "border border-[#DFE6ED] bg-white text-[#102A43]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <Card className="mt-5">
        <form onSubmit={submit}>
          <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">Account</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Contact name *">
              <Input data-testid="reg-name" required value={f.name} onChange={set("name")} />
            </Field>
            <Field label="Work email *">
              <Input data-testid="reg-email" required type="email" value={f.email} onChange={set("email")} />
            </Field>
            <Field label="Password *" hint="Minimum 8 characters">
              <Input data-testid="reg-password" required type="password" minLength={8} value={f.password} onChange={set("password")} />
            </Field>
            <Field label="Phone / WhatsApp">
              <Input data-testid="reg-phone" value={f.phone} onChange={set("phone")}
                placeholder={f.country === UAE ? "+971 …" : "Include country code, e.g. +44 …"} />
            </Field>
          </div>

          <h2 className="mt-8 font-display text-lg font-bold tracking-tight text-[#102A43]">Company</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Company name *">
              <Input data-testid="reg-company" required value={f.company_name} onChange={set("company_name")} />
            </Field>
            <Field label="Country *">
              <CountrySelect testId="reg-country" required value={f.country}
                onChange={(c) => setF({ ...f, country: c, emirate: c === UAE ? f.emirate : "" })} />
            </Field>
            <Field label={licenceLabel(f.country)}>
              <Input data-testid="reg-licence" required value={f.trade_licence} onChange={set("trade_licence")} />
            </Field>
            {f.country === UAE ? (
              <Field label="Emirate *">
                <Select data-testid="reg-emirate" required value={f.emirate} onChange={set("emirate")}
                  options={[["", "Select emirate"], ...EMIRATES.map((x) => [x, x])]} />
              </Field>
            ) : (
              <Field label="State / Province / Region *">
                <Input data-testid="reg-state" required value={f.state} onChange={set("state")}
                  placeholder="e.g. Ontario, Bavaria, Maharashtra" />
              </Field>
            )}
            <Field label="City *">
              <Input data-testid="reg-city" required value={f.city} onChange={set("city")}
                placeholder={f.country === UAE ? "e.g. Mussafah, Jebel Ali" : "e.g. Toronto"} />
            </Field>
            {role === "buyer" ? (
              <Field label="Industry">
                <Input data-testid="reg-industry" value={f.industry} onChange={set("industry")} placeholder="Oil & Gas, EPC, Power…" />
              </Field>
            ) : (
              <Field label="Account type *">
                <Select data-testid="reg-supplier-type" value={f.supplier_type} onChange={set("supplier_type")} options={SUPPLIER_TYPES} />
              </Field>
            )}
            <Field label="Website">
              <Input data-testid="reg-website" value={f.website} onChange={set("website")} />
            </Field>
          </div>

          {role === "supplier" && (
            <>
              <h2 className="mt-8 font-display text-lg font-bold tracking-tight text-[#102A43]">
                Supply capability
              </h2>
              <p className="mt-1 text-sm text-[#667788]">
                These fields drive RFQ matching — you only receive enquiries relevant to what you
                actually supply.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <CategoryPicker testId="reg-categories" label="Product categories *"
                    hint="Tick every category you can supply — this is what RFQ matching uses"
                    options={schema.published} value={f.categories}
                    onChange={(next) => setF({ ...f, categories: next })} />
                </div>
                <Field label="Subcategories" hint="Comma separated">
                  <Input data-testid="reg-subcategories" value={f.subcategories} onChange={set("subcategories")} placeholder="Armoured Glands, Barrier Glands" />
                </Field>
                <Field label="Brands manufactured / represented" hint="Comma separated">
                  <Input data-testid="reg-brands" value={f.brands} onChange={set("brands")} placeholder="CMP Products, AFTEC" />
                </Field>
                <Field label="Markets served" hint="Comma separated countries / cities you can deliver to">
                  <Input data-testid="reg-markets" value={f.markets_served} onChange={set("markets_served")} placeholder="uae, saudi arabia, india, united kingdom" />
                </Field>
                <Field label="Certifications" className="sm:col-span-2" hint="Comma separated">
                  <Textarea rows={2} data-testid="reg-certifications" value={f.certifications} onChange={set("certifications")} placeholder="ISO 9001:2015, IECEx QAR" />
                </Field>
              </div>
            </>
          )}

          {error && (
            <p data-testid="register-error" className="mt-5 rounded-[9px] bg-[#fdecec] px-3 py-2 text-sm text-[#B42318]">
              {error}
            </p>
          )}
          <Btn data-testid="register-submit-btn" loading={busy} className="mt-7 w-full sm:w-auto">
            {role === "supplier" ? "Join as a Founding Supplier" : "Create buyer account"}
          </Btn>
        </form>
        <p className="mt-5 text-sm text-[#667788]">
          Already registered?{" "}
          <Link to="/login" className="font-semibold text-[#147D78] underline">
            Sign in
          </Link>
        </p>
      </Card>
    </div>
  );
}
